import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className="filter-chevron"
    data-open={open}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#6b7280', flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface PreviewSectionProps {
  title: string;
  defaultOpen?: boolean;
  children?: React.ReactNode;
  summary?: string;
}

const PreviewSection: React.FC<PreviewSectionProps> = ({
  title,
  defaultOpen = true,
  children,
  summary,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="filter-section" style={{ padding: '12px 0' }}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '4px 0',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>{title}</div>
          {!isOpen && summary && (
            <div style={{ marginTop: 2, fontSize: 13, color: '#6b7280' }}>{summary}</div>
          )}
        </div>
        <ChevronIcon open={isOpen} />
      </button>
      <div className="filter-accordion-content" data-open={isOpen}>
        <div style={{ paddingTop: 12, paddingBottom: 4 }}>{children}</div>
      </div>
    </div>
  );
};

interface FilterPanelPreviewProps {
  mode: 'standard' | 'advanced' | 'toolbar';
  accentColor?: string;
  showColumnSwitcher?: boolean;
}

const ToolbarPreview: React.FC<{ accentColor: string; showColumnSwitcher: boolean }> = ({
  accentColor,
  showColumnSwitcher,
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      Kategori Sayfası Önizleme (Üst Çubuk)
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
      Sol panel yoktur. Filtreler ve sıralama, listenin üstünde sabit (yapışkan) bir çubukta açılır menüler olarak sunulur.
    </Typography>

    <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          p: 1,
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        {['Sırala', 'Marka', 'Fiyat', 'Kategori', 'Cam Kenarı'].map((chip) => (
          <Box
            key={chip}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 5,
              fontSize: 12,
              color: 'text.secondary',
            }}
          >
            {chip}
            <ChevronIcon open={false} />
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        {showColumnSwitcher && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[2, 3, 4].map((col) => (
              <Box
                key={col}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.75,
                  border: '1px solid',
                  borderColor: col === 4 ? 'grey.900' : 'grey.300',
                  bgcolor: col === 4 ? 'grey.900' : 'background.paper',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box
        className="category-layout-preview__products"
        style={{ '--filter-accent-color': accentColor } as React.CSSProperties}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index}>
            <div
              className="category-layout-preview__card"
              style={{ aspectRatio: 'auto', height: 56 }}
            />
            <div className="category-layout-preview__card-line" style={{ width: '70%' }} />
          </div>
        ))}
      </Box>
    </Box>
  </Box>
);

const FilterPanelPreview: React.FC<FilterPanelPreviewProps> = ({
  mode,
  accentColor = '#f27a1a',
  showColumnSwitcher = false,
}) => (mode === 'toolbar' ? (
  <ToolbarPreview accentColor={accentColor} showColumnSwitcher={showColumnSwitcher} />
) : (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      Kategori Sayfası Önizleme
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
      Ürün listesi filtre paneliyle üstten hizalanır. Sıralama ve kolon seçici breadcrumb satırında görünür.
    </Typography>

    <Box
      className="category-page-breadcrumb-zone"
      sx={{
        mb: 1.5,
        p: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <div className="category-page-header__row">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1 }}>
          Ana Sayfa &gt; Kategori
        </Typography>
        <div className="category-listing-toolbar">
          {showColumnSwitcher && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[2, 3, 4, 5, 6].map((col) => (
                <Box
                  key={col}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.75,
                    border: '1px solid',
                    borderColor: col === 4 ? 'grey.900' : 'grey.300',
                    bgcolor: col === 4 ? 'grey.900' : 'background.paper',
                  }}
                />
              ))}
            </Box>
          )}
          {mode === 'standard' && (
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: 12,
                color: 'text.secondary',
              }}
            >
              Sırala
            </Box>
          )}
        </div>
      </div>
    </Box>

    <Box
      className="category-layout-preview"
      sx={{
        p: 1.5,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
      }}
    >
      <Box
        className="filter-panel"
        sx={{
          width: { xs: '100%', sm: 200 },
          flexShrink: 0,
          p: 0,
          bgcolor: 'transparent',
        }}
        style={{ '--filter-accent-color': accentColor } as React.CSSProperties}
      >
        <div className="filter-panel-scroll filter-panel-scroll--hidden" style={{ maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>
          {mode === 'advanced' && (
            <PreviewSection title="Sırala" summary="Önerilen">
              <label className="filter-radio-option" style={{ marginBottom: 8 }}>
                <input type="radio" className="filter-radio" name="preview-sort" defaultChecked />
                <span>Önerilen</span>
              </label>
              <label className="filter-radio-option">
                <input type="radio" className="filter-radio" name="preview-sort" />
                <span>Artan Fiyat</span>
              </label>
            </PreviewSection>
          )}

          <PreviewSection title="Marka">
            <div className="filter-search-input-wrapper" style={{ marginBottom: 10 }}>
              <svg className="filter-search-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input className="filter-search-input" placeholder="Marka Ara" />
            </div>
            <label className="filter-checkbox-option" style={{ marginBottom: 8 }}>
              <input type="checkbox" className="filter-checkbox" defaultChecked />
              <span>La Roche Posay</span>
            </label>
            <label className="filter-checkbox-option">
              <input type="checkbox" className="filter-checkbox" />
              <span>NIVEA</span>
            </label>
          </PreviewSection>

          <PreviewSection title="Renk" defaultOpen={false}>
            <input className="filter-search-input" placeholder="Renk Ara" />
          </PreviewSection>

          <PreviewSection title="Fiyat" defaultOpen={false} />
        </div>
      </Box>

      <div className="category-layout-preview__products">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            <div
              className="category-layout-preview__card"
              style={{ aspectRatio: 'auto', height: 72 }}
            />
            <div className="category-layout-preview__card-line" style={{ width: '70%' }} />
          </div>
        ))}
      </div>
    </Box>
  </Box>
));

export default FilterPanelPreview;
